import { Plus, Trash2 } from "lucide-react";
import {
  CONDITION_OPS,
  CONDITION_OP_LABELS,
  type ConditionGroup,
  type ConditionLeaf,
  type ConditionOp,
  type FieldSpec,
} from "@ckgo2/shared";

const sel =
  "rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-sm";

// Operators that take no value input.
const NO_VALUE_OPS: ConditionOp[] = ["is_set", "is_empty"];

/** Flat all/any condition builder over the subject's field registry. Nested groups are supported by
 * the data model but the UI keeps a single level (covers every seed + the common case). */
export default function ConditionGroupEditor({
  value,
  onChange,
  fields,
}: {
  value: ConditionGroup | null;
  onChange: (v: ConditionGroup | null) => void;
  fields: FieldSpec[];
}) {
  const mode: "all" | "any" = value?.any ? "any" : "all";
  const leaves: ConditionLeaf[] = ((mode === "any" ? value?.any : value?.all) ?? []).filter(
    (n): n is ConditionLeaf => !("all" in n) && !("any" in n),
  );

  const emit = (m: "all" | "any", ls: ConditionLeaf[]) => {
    if (ls.length === 0) return onChange(null);
    onChange(m === "any" ? { any: ls } : { all: ls });
  };

  const updateLeaf = (i: number, patch: Partial<ConditionLeaf>) => {
    const next = leaves.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
    emit(mode, next);
  };

  return (
    <div className="space-y-2">
      {leaves.length > 1 && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          Match
          <select
            className={sel}
            value={mode}
            onChange={(e) => emit(e.target.value as "all" | "any", leaves)}
          >
            <option value="all">all (AND)</option>
            <option value="any">any (OR)</option>
          </select>
          of:
        </div>
      )}

      {leaves.map((leaf, i) => {
        const fieldSpec = fields.find((f) => f.key === leaf.field);
        return (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <select
              className={sel}
              value={leaf.field}
              onChange={(e) => updateLeaf(i, { field: e.target.value })}
            >
              {fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
            <select
              className={sel}
              value={leaf.op}
              onChange={(e) => updateLeaf(i, { op: e.target.value as ConditionOp })}
            >
              {CONDITION_OPS.map((op) => (
                <option key={op} value={op}>
                  {CONDITION_OP_LABELS[op]}
                </option>
              ))}
            </select>
            {!NO_VALUE_OPS.includes(leaf.op) &&
              (fieldSpec?.options ? (
                <select
                  className={sel}
                  value={String(leaf.value ?? "")}
                  onChange={(e) => updateLeaf(i, { value: e.target.value })}
                >
                  <option value="">—</option>
                  {fieldSpec.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className={sel}
                  value={String(leaf.value ?? "")}
                  placeholder={leaf.op === "in" ? "a, b, c" : "value"}
                  onChange={(e) =>
                    updateLeaf(i, {
                      value:
                        leaf.op === "in"
                          ? e.target.value.split(",").map((s) => s.trim())
                          : e.target.value,
                    })
                  }
                />
              ))}
            <button
              type="button"
              onClick={() =>
                emit(
                  mode,
                  leaves.filter((_, idx) => idx !== i),
                )
              }
              className="rounded p-1 text-gray-400 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        );
      })}

      <button
        type="button"
        onClick={() =>
          emit(mode, [...leaves, { field: fields[0]?.key ?? "status", op: "eq", value: "" }])
        }
        className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 text-xs"
      >
        <Plus className="h-3.5 w-3.5" /> Add condition
      </button>
    </div>
  );
}
